import React from "react";
import { TimeFormat } from "../../enum";
import { ExtendedCustomTimePickerProps, TimeInputService } from "./types";
import {
  parseTimeString,
  toTimeString,
  transformHourForApi,
  transformHourTo12HourFormat,
  parseMinute,
  parseSecond,
  getNumberRangeAsOptions,
  getDefaultTimeFormat
} from "./utils";
import { FormFieldOption } from "shared/form/fields/types";

export default function useTimeInputService(props: ExtendedCustomTimePickerProps): TimeInputService {
  const secondValue = props.selectedDate?.getSeconds() ?? 0;
  const [hourValue, minuteValue] = React.useMemo(() => parseTimeString(props.value), [props.value]);
  const [timeFormat, setTimeFormat] = React.useState<TimeFormat>(() => getDefaultTimeFormat(hourValue));

  const hourOptions: Array<FormFieldOption> = React.useMemo(() => {
    // To keep 12 on adding it manually
    return [{ label: "12", value: "12" }, ...getNumberRangeAsOptions(1, 11)];
  }, []);

  const minuteOptions: Array<FormFieldOption> = React.useMemo(() => getNumberRangeAsOptions(0, 59), []);

  const timeFormatOptions: Array<FormFieldOption> = React.useMemo(() => {
    return [
      {
        label: TimeFormat.AM,
        value: TimeFormat.AM
      },
      {
        label: TimeFormat.PM,
        value: TimeFormat.PM
      }
    ];
  }, []);

  const timeChangeHandler = React.useCallback(
    (hour: number, minute: number) => {
      props.onChange(toTimeString(hour, minute));
    },
    [props]
  );

  const hourChangeHandler = React.useCallback(
    (value: string) => {
      const parsedHourValue = transformHourForApi(value, timeFormat);
      timeChangeHandler(parsedHourValue, minuteValue);
      return parsedHourValue;
    },
    [minuteValue, timeChangeHandler, timeFormat]
  );

  const minuteChangeHandler = React.useCallback(
    (value: string) => {
      const parsedMinuteValue = parseMinute(value);
      timeChangeHandler(hourValue, parsedMinuteValue);
      return parsedMinuteValue;
    },
    [hourValue, timeChangeHandler]
  );

  const secondChangeHandler = React.useCallback(
    (value: string) => {
      const seconds = parseSecond(value);
      const date = props.selectedDate;
      if (date) {
        date.setSeconds(seconds);
        //Calling this,So that change is reflected in input
        timeChangeHandler(hourValue, minuteValue);
        props.changeDate(date);
      }
      return seconds;
    },
    [hourValue, minuteValue, props, timeChangeHandler]
  );

  const hourDisplayValue = React.useMemo(() => transformHourTo12HourFormat(hourValue), [hourValue]);

  const timeFormatChangeHandler = React.useCallback(
    newTimeFormat => {
      setTimeFormat(newTimeFormat);
      //set hour value
      let newHourValue = hourDisplayValue;
      if (newTimeFormat === TimeFormat.PM && newHourValue < 12) {
        //Add 12 in AM value when switching to PM except for 12
        newHourValue += 12;
      } else if (newTimeFormat === TimeFormat.AM && newHourValue === 12) {
        // 12 PM => 00 => 12AM
        newHourValue = 0;
      }
      timeChangeHandler(newHourValue, minuteValue);
    },
    [hourDisplayValue, minuteValue, timeChangeHandler]
  );

  return {
    hourValue,
    minuteValue,
    timeFormat,
    hourDisplayValue,
    secondValue,
    hourOptions,
    minuteOptions,
    timeFormatOptions,
    timeFormatChangeHandler,
    hourChangeHandler,
    minuteChangeHandler,
    secondChangeHandler
  };
}
